import React, { useCallback, useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Label } from '@components/styles.tsx';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgClose from '@icons/close.svg?react';
import { Pageable } from '@api/ringil-api.v2.tsx';
import { PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import clsx from 'clsx';
import { useDebounce } from 'use-debounce';
import { toTestIdSlug } from '@utils/testUtils';

export interface DropdownItem {
  id: string;
  value: string;
  isNew?: boolean;
}

interface EditableDropdownProps {
  loadOptionsFromApi: (filter: string, pageable: Pageable, onSuccess: ((pagedData: PagedData<DropdownItem>) => void)) => void;
  selectedOptions?: DropdownItem[];
  placeholder: string;
  label?: string;
  onSelect?: (values: DropdownItem[]) => void;
  onDelete?: (value: DropdownItem, onDeleteSuccess: () => void) => void;
  multiSelect?: boolean;
  mandatory?: boolean;
  note?: React.ReactNode;
  dataTestId?: string;
  dataTestIdList?: string;
  dataTestIdItemPrefix?: string;
}

const EditableDropdown: React.FC<EditableDropdownProps> = ({
                                                             loadOptionsFromApi,
                                                             selectedOptions = [],
                                                             onSelect,
                                                             onDelete,
                                                             placeholder,
                                                             label,
                                                             multiSelect = false,
                                                             mandatory = false,
                                                             note,
                                                             dataTestId,
                                                             dataTestIdList,
                                                             dataTestIdItemPrefix,
                                                           }) => {

  const { tr } = useTranslationLgs();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<DropdownItem[]>([]);
  const [filteredValues, setFilteredValues] = useState<DropdownItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownListRef = useRef<HTMLDivElement>(null);

  const scrollIntoView = (index: number) => {
    const dropdownList = document.querySelector('[data-dropdown-list]');
    const item = document.querySelector(`[data-dropdown-item="${index}"]`);

    if (dropdownList && item) {
      const dropdownRect = dropdownList.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();

      if (itemRect.top < dropdownRect.top) {
        dropdownList.scrollTop -= dropdownRect.top - itemRect.top;
      } else if (itemRect.bottom > dropdownRect.bottom) {
        dropdownList.scrollTop += itemRect.bottom - dropdownRect.bottom;
      }
    }
  };

  useEffect(() => {
    setSelectedValues(selectedOptions);
  }, [selectedOptions]);

  const loadOptions = useCallback((searchTerm: string, pageable: Pageable, appendData: boolean = false) => {
    loadOptionsFromApi(searchTerm, pageable, (pagedData) => {
      setFilteredValues(prev => appendData ? [...prev, ...pagedData.data] : pagedData.data);
      const calculatedTotalPages = pagedData.size > 0 ? Math.ceil(pagedData.total / pagedData.size) : 0;
      setTotalPages(calculatedTotalPages);
      setCurrentPage(pagedData.page);
      setIsLoadingMore(false);
    });
  }, [loadOptionsFromApi]);

  useEffect(() => {
    setCurrentPage(0);
    loadOptions(debouncedSearchTerm, { pageSize: 20, pageNumber: 0 }, false);
  }, [debouncedSearchTerm, loadOptions]);

  useEffect(() => {
    if (focusedIndex >= 0) {
      scrollIntoView(focusedIndex);
    }
  }, [focusedIndex]);

  useEffect(() => {
    const handleScroll = () => {
      if (!dropdownListRef.current || isLoadingMore) return;

      const { scrollTop, scrollHeight, clientHeight } = dropdownListRef.current;
      const scrollThreshold = 50; // pixels from bottom to trigger load

      if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
        // User is near the bottom, load next page if available
        if (currentPage < totalPages - 1) {
          setIsLoadingMore(true);
          loadOptions(debouncedSearchTerm, { pageSize: 20, pageNumber: currentPage + 1 }, true);
        }
      }
    };

    const dropdownList = dropdownListRef.current;
    if (dropdownList && isOpen) {
      dropdownList.addEventListener('scroll', handleScroll);
      return () => {
        dropdownList.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isOpen, currentPage, totalPages, isLoadingMore, debouncedSearchTerm, loadOptions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleSelectValue = (item: DropdownItem) => {
    let newSelectedValues: DropdownItem[];
    setSearchTerm('');

    if (multiSelect) {
      newSelectedValues = selectedValues.some(selected => selected.id === item.id)
        ? selectedValues.filter(selected => selected.id !== item.id)
        : [...selectedValues, item];
    } else {
      newSelectedValues = [item];
      setIsOpen(false);
    }

    setSelectedValues(newSelectedValues);
    if (onSelect) {
      onSelect(newSelectedValues);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);

    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleDeleteValue = (e: React.MouseEvent, item: DropdownItem) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(item, () => {
        setCurrentPage(0);
        loadOptions(debouncedSearchTerm, { pageSize: 20, pageNumber: 0 }, false);
      });
    }
  };

  const getHighlightedText = (text: string, searchTerm: string) => {
    if (!searchTerm) return <>{text}</>;

    const index = text.toLowerCase().indexOf(searchTerm.toLowerCase());
    if (index === -1) return <>{text}</>;

    return (
      <>
        {text.slice(0, index)}
        <span className="text-content-primary font-bold">{text.slice(index, index + searchTerm.length)}</span>
        {text.slice(index + searchTerm.length)}
      </>
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && searchTerm === '' && selectedValues.length > 0) {
      e.preventDefault();
      const lastItem = selectedValues[selectedValues.length - 1];
      handleChipRemove(lastItem);
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        setFocusedIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev =>
          prev < filteredValues.length - 1 ? prev + 1 : prev,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredValues.length) {
          handleSelectValue(filteredValues[focusedIndex]);
        } else if (searchTerm.trim()) {
          const newItem: DropdownItem = {
            id: searchTerm.trim(),
            value: searchTerm.trim(),
            isNew: true,
          };
          handleSelectValue(newItem);
        }
        setSearchTerm('');
        setFocusedIndex(-1);
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const handleChipRemove = (item: DropdownItem) => {
    const newSelected = selectedValues.filter(p => p.id !== item.id);
    setSelectedValues(newSelected);
    if (onSelect) {
      onSelect(newSelected);
    }
    setSearchTerm('');
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (containerRef.current && containerRef.current.contains(e.relatedTarget as Node)) {
      return;
    }

    if (e.target.value.trim()) {
      const newItem: DropdownItem = {
        id: searchTerm.trim(),
        value: searchTerm.trim(),
        isNew: true,
      };
      setSearchTerm('');
      handleSelectValue(newItem);
    }
  };

  return (
    <>
      <div className="relative w-full" ref={containerRef}>
        {label && (
          <Label>
            {label}
            {mandatory && <>*</>}
          </Label>
        )}
        <div
          className={clsx(
            'flex items-center rounded border border-solid  cursor-text hover:border-content-brand',
            isOpen ? 'border-content-brand' : 'border-content-secondary',
            mandatory && selectedValues.length === 0 ? 'bg-complementary-warningMedium' : 'bg-white'
          )}
        >
          <div className="flex flex-wrap gap-2 p-1 items-center w-full min-h-[38px]">
            {selectedValues.map(item => (
              <div
                className="flex items-center bg-base-tertiary rounded py-1 px-2 m-0.5 gap-2"
                key={item.id}
                data-testid={dataTestIdItemPrefix ? `${dataTestIdItemPrefix}-selected-${toTestIdSlug(item.value)}` : undefined}
              >
                <div className="text-sm text-content-primary">{item.value}</div>
                <SvgClose className="text-content-brand cursor-pointer hover:text-complementary-error"
                          onClick={() => handleChipRemove(item)} />
              </div>
            ))}
            <input
              data-testid={dataTestId}
              className="flex-1 min-w-[50px] p-2 font-['DM Sans',serif] text-sm border-0 outline-none bg-transparent placeholder:text-content-secondary"
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              placeholder={selectedValues.length === 0 ? placeholder : ''}
              onClick={() => setIsOpen(prev => !prev)}
            />
          </div>
        </div>

        {isOpen && (
          <div
            ref={dropdownListRef}
            data-dropdown-list
            data-testid={dataTestIdList}
            className="absolute top-[calc(100%+4px)] left-0 right-0 max-h-[200px] overflow-y-auto bg-white border border-base-secondary border-t-0 rounded-b-[4px] z-10 shadow-[0_4px_8px_rgba(0,0,0,0.1)] -mt-[1px]"
          >
            {filteredValues.length > 0 ? (
              <>
                {filteredValues.map((item, index) => (
                  <div
                    key={item.id}
                    data-dropdown-item={index}
                    data-testid={dataTestIdItemPrefix ? `${dataTestIdItemPrefix}-value-${toTestIdSlug(item.value)}` : undefined}
                    className={clsx(
                      'p-3 cursor-pointer transition-colors duration-200 flex justify-between items-center hover:bg-base-tertiary',
                      index === focusedIndex ? 'bg-base-tertiary' : selectedValues.some(selected => selected.id === item.id) && 'bg-content-brandMedium',
                    )}
                    onClick={() => handleSelectValue(item)}
                  >
                    <span>
                      {getHighlightedText(item.value, searchTerm.split(',')[searchTerm.split(',').length - 1].trim())}
                    </span>
                    {onDelete && (
                      <SvgDelete
                        className="text-content-secondary cursor-pointer hover:text-complementary-error"
                        onClick={(e) => handleDeleteValue(e, item)}
                      />
                    )}
                  </div>
                ))}
                {isLoadingMore && (
                  <div className="text-content-secondary text-center p-3 italic">
                    {tr('EditableDropdown.loading', 'Loading...')}
                  </div>
                )}
              </>
            ) : (
              <div
                className="text-content-secondary text-center p-3 italic"
              >
                {tr('EditableDropdown.noResults', 'No items found')}
              </div>
            )}
          </div>
        )}
      </div>
      {note}
    </>
  );
};

export default EditableDropdown;